import React from "react";
import { Terminal, Zap } from "lucide-react";
import CodeBlock from "../components/CodeBlock";
import { Card } from "../components/ui/card";
import { cliCommands } from "../mockData";

const CLI = () => {
  return (
    <div className="prose prose-invert max-w-none">
      {/* Header */}
      <div className="not-prose mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
          <Terminal className="h-4 w-4" />
          CLI Tool
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          AxisNet CLI
        </h1>
        <p className="text-xl text-slate-400">
          Powerful command-line tool for scaffolding features, domains, and
          entities with intelligent code generation.
        </p>
      </div>

      {/* Overview */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">
          What is AxisNet.Cli?
        </h2>
        <p className="text-slate-300 mb-6">
          AxisNet.Cli is a standalone console application that accelerates
          development by generating boilerplate code following clean
          architecture principles. It can work independently or connect to your
          online AxisNet project to pull domain definitions from the cloud.
        </p>

        <p className="text-slate-300 mb-6">
          Once installed, you can launch the interactive AxisNet shell simply by
          running
          <span className="text-emerald-300 font-semibold"> axisnet </span> in
          your terminal. After the shell starts, you can directly execute
          commands such as
          <span className="text-emerald-300 font-semibold"> init</span>,
          <span className="text-emerald-300 font-semibold"> add feature</span>,
          <span className="text-emerald-300 font-semibold"> info</span>, and
          more—no prefix required.
        </p>

        <div className="not-prose mb-6">
          <Card className="p-6 bg-emerald-900/20 border-emerald-500/30">
            <h3 className="text-lg font-semibold text-emerald-300 mb-3">
              Installation
            </h3>
            <CodeBlock code="dotnet tool install --global AxisNet.Cli" />

            <p className="text-sm text-emerald-200 mt-3">
              Verify installation:
            </p>
            <div className="mt-2">
              <CodeBlock code="axisnet --version" />
            </div>

            <p className="text-sm text-emerald-200 mt-4">
              Start the AxisNet CLI shell:
            </p>
            <div className="mt-2">
              <CodeBlock code="axisnet" />
            </div>
          </Card>
        </div>
      </section>

      {/* Key Features */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-6">
          <Zap className="inline h-8 w-8 mr-2 text-emerald-400" />
          Key Features
        </h2>

        <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 bg-slate-900/50 border-slate-800">
            <h3 className="text-lg font-semibold text-white mb-2">
              Instant Scaffolding
            </h3>
            <p className="text-sm text-slate-400">
              Generate complete domain layers, entities, commands, queries, and
              repositories with a single command.
            </p>
          </Card>

          <Card className="p-6 bg-slate-900/50 border-slate-800">
            <h3 className="text-lg font-semibold text-white mb-2">
              Cloud Synchronization
            </h3>
            <p className="text-sm text-slate-400">
              Connect to your online AxisNet project and pull domain definitions
              or manually.
            </p>
          </Card>

          <Card className="p-6 bg-slate-900/50 border-slate-800">
            <h3 className="text-lg font-semibold text-white mb-2">
              Template System
            </h3>
            <p className="text-sm text-slate-400">
              Customize code generation templates to match your team's coding
              standards and preferences.
            </p>
          </Card>

          <Card className="p-6 bg-slate-900/50 border-slate-800">
            <h3 className="text-lg font-semibold text-white mb-2">
              Clean Architecture
            </h3>
            <p className="text-sm text-slate-400">
              Automatically organizes generated code into Domain, Application,
              and Infrastructure layers.
            </p>
          </Card>
        </div>
      </section>

      {/* Command Reference */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-6">
          Command Reference
        </h2>

        <div className="not-prose space-y-6">
          {cliCommands.map((cmd, idx) => (
            <Card key={idx} className="p-6 bg-slate-900/50 border-slate-800">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Terminal className="h-5 w-5 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    <code className="text-emerald-400">{cmd.command}</code>
                  </h3>
                  <p className="text-slate-400 mb-4">{cmd.description}</p>
                  <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                    <code className="text-sm text-slate-300 font-mono">
                      {cmd.example}
                    </code>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Configuration */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">Configuration</h2>
        <p className="text-slate-300 mb-6">
          AxisNet.Cli stores configuration in{" "}
          <code className="text-emerald-400">axisnet.json</code> in your
          solution root. You can customize:
        </p>

        <div className="not-prose">
          <CodeBlock
            language="json"
            code={`{
  "Initialize": {
    "ProjectName": "",
    "MainPath": "",
    "TestPath": "",
    "DomainPath": "",
    "ApplicationPath": "",
    "InfrastructurePath": ""
  }
}`}
          />
        </div>
      </section>
    </div>
  );
};

export default CLI;
